'use client'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useFormikContext } from 'formik'
import { loadSector, loadSeatsInfoSell } from '~/state/modules/planimetry'
import { loadTicket } from '~/state/modules/events'
import {
  Container,
  Box,
  CircularProgress,
  Typography,
  Divider
} from '@material-ui/core'
import SeatPlanner from './components/SeatPlanner'
import ControlPanel from './components/ControlPanel'
import { SeatStatus, PositionDescriptor } from 'config/constants'

export default function SeatSettings() {
  const { sectorId, eventId } = useParams()
  const dispatch = useDispatch()
  const { pendingSector, pendingSeatsInfo } = useSelector(
    (state) => state.planimetry
  )
  const { pendingTicket, ticket } = useSelector((state) => state.events)
  const [seats, setSeats] = useState([])
  const [seatGrid, setSeatGrid] = useState([])
  const [rows, setRows] = useState()
  const [columns, setColumns] = useState()
  const [amountSelectedSeats, setAmountSelectedSeats] = useState(0)
  const [seatsSelected, setSeatsSelected] = useState([])
  const [soldUser, setSoldUser] = useState(null)
  const [selectAllChecked, setSelectAllChecked] = useState(false)
  const [rowDescriptor, setRowDescriptor] = useState(PositionDescriptor.Numeric)
  const [columnDescriptor, setColumnDescriptor] = useState(
    PositionDescriptor.Numeric
  )
  const [resetUserSearch, setResetUserSearch] = useState(false)
  const selectedStatus = SeatStatus.ToSell
  const seatPlannerRef = useRef(null)
  const { setFieldValue } = useFormikContext()

  const handleSelectAll = () => {
    if (!selectAllChecked) {
      // 1) pasar todos los Available a ToSell
      const updatedSeats = seats.map((s) =>
        s.status === SeatStatus.Available
          ? { ...s, status: SeatStatus.ToSell }
          : s
      )
      setSeats(updatedSeats)
      initializeSeatGrid(updatedSeats, rows, columns)

      // 2) generar la lista de seleccionados y el contador
      const selected = updatedSeats
        .filter((s) => s.status === SeatStatus.ToSell)
        .map((s) => ({ seatId: s.id, description: s.description }))
      setSeatsSelected(selected)
      setAmountSelectedSeats(selected.length)

      setSelectAllChecked(true)
    } else {
      // revertir: todos los ToSell vuelven a Available
      const updatedSeats = seats.map((s) =>
        s.status === SeatStatus.ToSell
          ? { ...s, status: SeatStatus.Available }
          : s
      )
      setSeats(updatedSeats)
      initializeSeatGrid(updatedSeats, rows, columns)

      setSeatsSelected([])
      setAmountSelectedSeats(0)
      setSelectAllChecked(false)
    }
  }

  const updateSeatStatus = useCallback(
    (row, column, status) => {
      const seat = seatGrid[row - 1]?.[column - 1]
      if (!seat || seat.status === status) return
      const updatedSeat = { ...seat, status }
      setSeats((prev) =>
        prev.map((s) =>
          s.row === row && s.column === column ? updatedSeat : s
        )
      )
      setSeatGrid((prev) => {
        const newGrid = prev.map((r) => [...r])
        newGrid[row - 1][column - 1] = updatedSeat
        return newGrid
      })
    },
    [seatGrid]
  )

  const selectedStatusRef = useRef(selectedStatus)
  useEffect(() => {
    selectedStatusRef.current = selectedStatus
  }, [selectedStatus])

  const handleSoldClick = useCallback(
    (seatId, userId, fullName, identificationType, documentNumber) => {
      setSeats((prevSeats) => {
        // buscar por id (recomendado)
        const clickedPrev = prevSeats.find((s) => s.id === seatId)
        const prevSelected = prevSeats.find(
          (s) => s.status === SeatStatus.SoldSelected
        )

        // nuevo arreglo de seats: toggle clicked, y revertir cualquier SoldSelected previo
        const newSeats = prevSeats.map((s) => {
          if (s.id === seatId) {
            if (s.status === SeatStatus.SoldSelected)
              return { ...s, status: SeatStatus.Sold }
            if (s.status === SeatStatus.Sold)
              return { ...s, status: SeatStatus.SoldSelected }
            return s
          }
          if (s.status === SeatStatus.SoldSelected)
            return { ...s, status: SeatStatus.Sold }
          return s
        })

        const clickedUpdated = newSeats.find((s) => s.id === seatId)
        const prevSelectedUpdated = prevSelected
          ? newSeats.find((s) => s.id === prevSelected.id)
          : null

        // Actualizamos solo las celdas necesarias del seatGrid
        setSeatGrid((prevGrid) => {
          // Flujo normal: clonamos filas y actualizamos solamente las celdas cambiadas
          const newGrid = prevGrid.map((r) => [...r])
          if (clickedUpdated && clickedUpdated.row && clickedUpdated.column) {
            newGrid[clickedUpdated.row - 1][clickedUpdated.column - 1] =
              clickedUpdated
          }
          if (
            prevSelectedUpdated &&
            prevSelectedUpdated.row &&
            prevSelectedUpdated.column
          ) {
            newGrid[prevSelectedUpdated.row - 1][
              prevSelectedUpdated.column - 1
            ] = prevSelectedUpdated
          }
          return newGrid
        })

        // actualizar soldUser según toggle
        if (clickedPrev?.status === SeatStatus.SoldSelected) {
          setSoldUser(null) // deseleccioné el mismo
        } else {
          setSoldUser({
            userId,
            fullName,
            identificationType,
            documentNumber
          })
        }

        return newSeats
      })
    },
    []
  )

  const handleSeatClick = useCallback(
    (seatId, row, column, newStatus, description) => {
      if (newStatus === SeatStatus.ToSell) {
        setAmountSelectedSeats((prev) => prev + 1)
        setSeatsSelected((prev) => [
          ...prev,
          { seatId, description: description }
        ])
      } else {
        setSeatsSelected((prev) => prev.filter((s) => s.seatId !== seatId))
        setAmountSelectedSeats((prev) => prev - 1)
      }
      updateSeatStatus(row, column, newStatus)
    },
    [updateSeatStatus]
  )

  useEffect(() => {
    async function fetchSectorAndSeats() {
      const sector = await dispatch(loadSector(sectorId))
      setRows(sector.Row)
      setColumns(sector.Column)
      setRowDescriptor(sector.RowDescriptor)
      setColumnDescriptor(sector.ColumnDescriptor)

      const loadedSeats = await dispatch(loadSeatsInfoSell(sectorId))
      if (loadedSeats && loadedSeats.length > 0) {
        await dispatch(loadTicket(eventId, loadedSeats[0].ticketId))
        setSeats(loadedSeats)
        initializeSeatGrid(loadedSeats, sector.Row, sector.Column)
      } else {
        initializeSeats(sector.Row, sector.Column)
      }
    }
    fetchSectorAndSeats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sectorId])

  useEffect(() => {
    setFieldValue('Seats', seats)
    setFieldValue('Row', rows)
    setFieldValue('Column', columns)
  }, [seats, rows, columns, setFieldValue])

  const initializeSeats = (numRows, numColumns) => {
    const newSeats = Array.from({ length: numRows * numColumns }, (_, idx) => ({
      row: Math.floor(idx / numColumns) + 1,
      column: (idx % numColumns) + 1,
      status: SeatStatus.Available
    }))
    setSeats(newSeats)
    initializeSeatGrid(newSeats, numRows, numColumns)
  }

  const initializeSeatGrid = (seatList, numRows, numColumns) => {
    const grid = Array.from({ length: numRows }, () =>
      Array(numColumns).fill(null)
    )
    seatList.forEach((seat) => {
      grid[seat.row - 1][seat.column - 1] = seat
    })
    setSeatGrid(grid)
  }

  const nonEmptySeats = seats.filter((seat) => seat.status !== SeatStatus.Empty)
  const totalNonEmpty = nonEmptySeats.length

  const statusCounts = seats.reduce((acc, seat) => {
    let statusKey
    if (
      seat.status === SeatStatus.Available ||
      seat.status === SeatStatus.ToSell
    ) {
      statusKey = SeatStatus.Available
    } else if (
      seat.status === SeatStatus.Sold ||
      seat.status === SeatStatus.SoldSelected
    ) {
      statusKey = SeatStatus.Sold
    } else {
      statusKey = seat.status
    }
    const prev = acc[statusKey]?.count || 0
    const count = prev + 1
    let percent = 0
    if (totalNonEmpty > 0) {
      percent = (count / totalNonEmpty) * 100
      percent = percent % 1 === 0 ? percent.toFixed(0) : percent.toFixed(2)
      percent = Number(percent)
    }
    acc[statusKey] = { count, percent }
    return acc
  }, {})

  Object.keys(statusCounts).forEach((status) => {
    const { count, percent } = statusCounts[status]
    if (status === SeatStatus.Empty.toString()) {
      statusCounts[status] = `${count}`
    } else {
      statusCounts[status] = `${count} (${percent}%)`
    }
  })

  const handleClearSelectedSeats = () => {
    setSeatsSelected([])
    setAmountSelectedSeats(0)
    setFieldValue('Seats', [])
    setFieldValue('Row', rows)
    setFieldValue('Column', columns)
    if (seatPlannerRef.current) {
      seatPlannerRef.current.clearSelectedSeats()
    } else {
      console.warn('seatPlannerRef is not set')
    }
  }

  const handleAfterSuccess = async () => {
    setResetUserSearch(true)
    handleClearSelectedSeats()
    const loadedSeats = await dispatch(loadSeatsInfoSell(sectorId))
    setSeats(loadedSeats)
    initializeSeatGrid(loadedSeats, rows, columns)
  }

  useEffect(() => {
    if (resetUserSearch) setResetUserSearch(false)
  }, [resetUserSearch])

  return (
    <Container maxWidth='lg'>
      <Box sx={{ my: 2 }}>
        <ControlPanel
          selectedStatus={selectedStatus}
          statusCounts={statusCounts}
          seatsSelected={seatsSelected}
          clearSelectedSeats={handleClearSelectedSeats}
          selectAllChecked={selectAllChecked}
          onSelectAll={handleSelectAll}
          amountSelectedSeats={amountSelectedSeats}
          sectorId={sectorId}
          ticketId={ticket?.Id}
          totalToPay={ticket?.Price * amountSelectedSeats || 0}
          onSuccess={handleAfterSuccess}
          resetUserSearch={resetUserSearch}
          soldUser={soldUser}
          clearSoldUser={() => setSoldUser(null)}
        />
        <Divider style={{ marginBlock: 16 }} />
        {pendingSector ||
        pendingSeatsInfo ||
        pendingTicket ||
        !seatGrid ||
        seatGrid.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
            <CircularProgress size={24} style={{ marginRight: '.5rem' }} />
            <Typography>Cargando...</Typography>
          </Box>
        ) : (
          <SeatPlanner
            ref={seatPlannerRef}
            seatGrid={seatGrid}
            rows={rows}
            columns={columns}
            columnDescriptor={columnDescriptor}
            rowDescriptor={rowDescriptor}
            onSeatClick={handleSeatClick}
            onSoldClick={handleSoldClick}
          />
        )}
      </Box>
    </Container>
  )
}
